// frontend/src/hooks/useMediaQuery.ts
// Custom hook for responsive design with proper React state management

import { useState, useEffect } from 'react';

export const BREAKPOINTS = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  wide: 1440,
  ultrawide: 1920
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Hook to detect screen size breakpoints
 * @param query - Media query string or breakpoint name
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string | Breakpoint): boolean {
  // Convert breakpoint names to media queries
  const mediaQuery = typeof query === 'string' && query in BREAKPOINTS
    ? `(min-width: ${BREAKPOINTS[query as Breakpoint]}px)`
    : query;

  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(mediaQuery).matches;
    }
    return false;
  });

  useEffect(() => {
    const mediaQueryList = window.matchMedia(mediaQuery);
    const documentChangeHandler = () => setMatches(mediaQueryList.matches);

    // Set initial value
    setMatches(mediaQueryList.matches);

    // Modern browsers
    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener('change', documentChangeHandler);
    } else {
      // Fallback for older browsers
      mediaQueryList.addListener(documentChangeHandler);
    }

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener('change', documentChangeHandler);
      } else {
        mediaQueryList.removeListener(documentChangeHandler);
      }
    };
  }, [mediaQuery]);

  return matches;
}

/**
 * Hook to get current breakpoint
 * @returns Current breakpoint name
 */
export function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
    if (typeof window === 'undefined') return 'mobile';
    
    const width = window.innerWidth;
    if (width >= BREAKPOINTS.ultrawide) return 'ultrawide';
    if (width >= BREAKPOINTS.wide) return 'wide';
    if (width >= BREAKPOINTS.desktop) return 'desktop';
    if (width >= BREAKPOINTS.tablet) return 'tablet';
    return 'mobile';
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= BREAKPOINTS.ultrawide) setBreakpoint('ultrawide');
      else if (width >= BREAKPOINTS.wide) setBreakpoint('wide');
      else if (width >= BREAKPOINTS.desktop) setBreakpoint('desktop');
      else if (width >= BREAKPOINTS.tablet) setBreakpoint('tablet');
      else setBreakpoint('mobile');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
}

/**
 * Hook to check if mobile (< tablet breakpoint)
 */
export function useIsMobile(): boolean {
  return !useMediaQuery('tablet');
}

/**
 * Hook to check if tablet (>= tablet, < desktop)
 */
export function useIsTablet(): boolean {
  const isTablet = useMediaQuery('tablet');
  const isDesktop = useMediaQuery('desktop');
  return isTablet && !isDesktop;
}

/**
 * Hook to check if desktop (>= desktop)
 */
export function useIsDesktop(): boolean {
  return useMediaQuery('desktop');
}
